/**
 * Lightweight global hook that lazily fetches the PJR catalog summary
 * (`/api/pjr/admin/products`) for any admin so the navbar/admin page can
 * show a live "(N)" badge next to the CMS shortcut. The endpoint requires
 * an admin role, so for non-admins this returns null without a network call.
 *
 * The fetch is fire-and-forget on mount and refresh-on-focus so the badge
 * stays current after the admin creates or deletes a product on a sibling
 * tab. We avoid polling — the catalog is far from a hot dataset.
 */
import { useEffect, useState } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';

const API = process.env.REACT_APP_BACKEND_URL;

export function usePJRSummary() {
  const { user } = useAuth();
  const isAdmin = user && (user.role === 'admin' || user.role === 'super_admin');
  const [summary, setSummary] = useState(null);

  useEffect(() => {
    if (!isAdmin) { setSummary(null); return; }
    let alive = true;
    const fetchOnce = async () => {
      try {
        const r = await axios.get(`${API}/api/pjr/admin/products`, { withCredentials: true });
        if (alive) setSummary(r.data?.summary || null);
      } catch {
        // silent — admin gate elsewhere will surface auth errors if needed
      }
    };
    fetchOnce();
    const onFocus = () => fetchOnce();
    window.addEventListener('focus', onFocus);
    return () => { alive = false; window.removeEventListener('focus', onFocus); };
  }, [isAdmin]);

  return summary;
}
